"use client";

import React, { useState, useEffect } from "react";
import Link from "next/link";
import Image from "next/image";
import { usePathname } from "next/navigation";
import {
  Home,
  Users,
  Handshake,
  TreePine,
  FolderOpen,
  Library,
  Users2,
  Briefcase,
  Newspaper,
  Shield,
  Phone,
  Menu,
  X,
} from "lucide-react";
import { landingConfigApi } from "@/api";
import { ConfigItemImage } from "@/types/config";
import { getImageUrl, isValidImageUrl } from "@/utils/image";

interface MenuItem {
  icon: React.ReactNode;
  label: string;
  href: string;
}

const menuItems: MenuItem[] = [
  {
    icon: <Home className="w-5 h-5" />,
    label: "Trang chủ",
    href: "/",
  },
  {
    icon: <Users className="w-5 h-5" />,
    label: "Về chúng tôi",
    href: "/about",
  },
  {
    icon: <Handshake className="w-5 h-5" />,
    label: "Đối tác",
    href: "/partners",
  },
  {
    icon: <TreePine className="w-5 h-5" />,
    label: "Lộ trình Xanh",
    href: "/green-roadmap",
  },
  {
    icon: <FolderOpen className="w-5 h-5" />,
    label: "Dự án & sản phẩm",
    href: "/projects",
  },
  {
    icon: <Library className="w-5 h-5" />,
    label: "Thư viện",
    href: "/library",
  },
  // {
  //   icon: <Users2 className="w-5 h-5" />,
  //   label: "Cộng đồng xanh",
  //   href: "/community",
  // },
  {
    icon: <Briefcase className="w-5 h-5" />,
    label: "Tuyển dụng",
    href: "/careers",
  },
  { icon: <Newspaper className="w-5 h-5" />, label: "Tin tức", href: "/news" },
  {
    icon: <Shield className="w-5 h-5" />,
    label: "Chính sách & điều khoản",
    href: "/policies",
  },
  { icon: <Phone className="w-5 h-5" />, label: "Liên hệ", href: "/contact" },
];

const Sidebar: React.FC = () => {
  const pathname = usePathname();
  const [logoConfig, setLogoConfig] = useState<ConfigItemImage | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);
  useEffect(() => {
    const fetchLogo = async () => {
      try {
        setIsLoading(true);
        // Fetch logo config with type safety
        const response = await landingConfigApi.findOne({
          where: {
            key: { equals: "logo" },
          },
        });

        if (response.success && response.data) {
          // Cast to ConfigItemImage type
          const configData = response.data as unknown as ConfigItemImage;

          // Validate that it's an image type config with valid URL
          if (configData.type === "image" && configData.imageValue?.url) {
            setLogoConfig(configData);
          } else {
            console.warn(
              "Logo config is not an image type or missing imageValue/url"
            );
          }
        } else if (!response.success) {
          console.error("Failed to fetch logo:", response.error?.message);
        }
      } catch (error) {
        console.error("Error fetching logo from API:", error);
      } finally {
        setIsLoading(false);
      }
    };

    fetchLogo();
  }, []);

  // Helper to check if we have a valid logo
  const hasValidLogo =
    logoConfig?.imageValue?.url && isValidImageUrl(logoConfig.imageValue.url);

  // Close mobile menu when route changes
  useEffect(() => {
    setIsMobileMenuOpen(false);
  }, [pathname]);

  // Prevent body scroll when mobile menu is open
  useEffect(() => {
    if (isMobileMenuOpen) {
      document.body.style.overflow = "hidden";
    } else {
      document.body.style.overflow = "unset";
    }
    return () => {
      document.body.style.overflow = "unset";
    };
  }, [isMobileMenuOpen]);

  return (
    <>
      {/* Mobile Menu Button */}
      <button
        onClick={() => setIsMobileMenuOpen(!isMobileMenuOpen)}
        className="lg:hidden fixed top-4 left-4 z-50 p-2 bg-white rounded-lg shadow-lg hover:bg-gray-50 transition-colors"
        aria-label="Toggle menu"
      >
        {isMobileMenuOpen ? (
          <X className="w-6 h-6 text-gray-700" />
        ) : (
          <Menu className="w-6 h-6 text-gray-700" />
        )}
      </button>

      {/* Mobile Backdrop Overlay */}
      {isMobileMenuOpen && (
        <div
          className="lg:hidden fixed inset-0 bg-black/50 backdrop-blur-sm z-40 transition-opacity"
          onClick={() => setIsMobileMenuOpen(false)}
        />
      )}

      {/* Sidebar */}
      <aside
        className={`
          w-64 bg-white/95 backdrop-blur-sm h-screen fixed left-0 top-0 flex flex-col shadow-xl z-40
          transition-transform duration-300 ease-in-out
          lg:translate-x-0
          ${isMobileMenuOpen ? "translate-x-0" : "-translate-x-full"}
        `}
      >
        {/* Logo Section */}
        <div className="flex justify-center items-center p-4">
          {hasValidLogo && !isLoading ? (
            <Image
              src={getImageUrl(logoConfig!.imageValue.url)}
              alt={logoConfig!.imageValue.alt || logoConfig!.label || "Logo"}
              title={logoConfig!.label}
              height={100}
              width={130}
            />
          ) : (
            <div className="w-10 h-10 bg-gray-200 rounded-full"></div>
          )}
        </div>

        {/* Navigation Menu */}
        <nav className="flex-1 overflow-y-auto">
          <ul className="space-y-1 px-3">
            {menuItems.map((item, index) => {
              const isActive = pathname === item.href;

              return (
                <li key={index}>
                  <Link
                    href={item.href}
                    className={`
                    flex items-center px-4 py-2.5 rounded-lg transition-all duration-200
                    ${
                      isActive
                        ? "bg-gradient-to-r from-green-50 to-green-100 text-green-700 font-medium shadow-sm"
                        : "text-gray-600 hover:bg-gray-50 hover:text-gray-900"
                    }
                  `}
                  >
                    <span
                      className={`${
                        isActive ? "text-green-600" : "text-gray-400"
                      }`}
                    >
                      {item.icon}
                    </span>
                    <span className="ml-3">{item.label}</span>
                  </Link>
                </li>
              );
            })}
          </ul>
        </nav>

        {/* Footer */}
        <div className="p-4 border-t border-gray-100">
          <p className="text-xs text-gray-400 text-center">© 2025 KTX Group</p>
        </div>
      </aside>
    </>
  );
};

export default Sidebar;
